import { FieldArray, Formik } from 'formik';
import { Button, Card, CardBody, CardFooter, Heading, IconChevron, Input } from '../../ui';
import * as yup from 'yup';
import { careTeamOptionsList } from './types';
import { emailRegex } from '../../utilities/regex';
import { RecipientType, recipientTypeValues } from '../../api/consent/types';
import { useReferOrgInfo } from './useReferOrgInfo';

const validationSchema = yup.object({
    careTeam: yup
        .array()
        .of(
            yup.object({
                recipientType: yup
                    .string()
                    .oneOf([...recipientTypeValues])
                    .required('Required'),
                recipientName: yup.string(),
                recipientEmail: yup.string().matches(emailRegex, 'Invalid email'),
            })
        )
        .max(recipientTypeValues.length),
});

interface Recipient {
    recipientType: RecipientType;
    recipientName: string;
    recipientEmail: string;
    organisationId?: number;
}

export interface CareTeamDetailsFormValues {
    careTeam: Recipient[];
}

export interface CareTeamDetailsProps {
    initialValues?: CareTeamDetailsFormValues;
    childName: string;
    careTeam: string[];
    onSubmit: (values: CareTeamDetailsFormValues) => Promise<void> | void;
    onBack: (values: CareTeamDetailsFormValues) => void;
}

export function CareTeamDetails({ onSubmit, onBack, careTeam, childName, initialValues }: CareTeamDetailsProps) {
    const careTeamItems = careTeamOptionsList.filter(({ recipientType }) => careTeam.includes(recipientType));
    const { referOrgId, referOrgName } = useReferOrgInfo();
    const defaultDetails: Recipient[] = careTeamItems.map((careTeamItem) => {
        const initial = (initialValues?.careTeam || []).find(
            ({ recipientType }) => recipientType === careTeamItem.recipientType
        );
        const out: Recipient = {
            recipientType: careTeamItem.recipientType,
            recipientName: initial?.recipientName || '',
            recipientEmail: initial?.recipientEmail || '',
        };
        if (careTeamItem.recipientType === 'referringOrg') {
            out.organisationId = referOrgId;
            out.recipientName = referOrgName;
        }
        return out;
    });
    const defaultValues = { careTeam: defaultDetails };

    return (
        <div className="max-w-[684px] mx-auto my-20 space-y-6">
            <div className="text-center space-y-6">
                <Heading level={1}>Create {childName}'s care team</Heading>
                <p>
                    We recommend forming a care team, which is clinically proven to benefit progress, as our therapist
                    can notify other invested parties to support {childName}’s growth.
                </p>
            </div>

            <Formik initialValues={defaultValues} onSubmit={onSubmit} validationSchema={validationSchema}>
                {({ handleChange, isSubmitting, handleSubmit, values }) => (
                    <form onSubmit={handleSubmit} noValidate>
                        <Card>
                            <CardBody>
                                <div className="space-y-6">
                                    {careTeamItems.length === 0 ? (
                                        <>
                                            <p>You’ve not selected anybody for {childName}’s Care Team.</p>
                                            <p>
                                                If you’d like to add people at a later date please get in touch with the
                                                therapist directly.
                                            </p>
                                        </>
                                    ) : (
                                        <p>
                                            Please add the details of {childName}’s Care Team below. If you don’t have
                                            these now, you can add them at a later date.{' '}
                                        </p>
                                    )}
                                    <FieldArray name="careTeam">
                                        {() =>
                                            careTeamItems.map(({ label, recipientType }, index) => (
                                                <div className="space-y-4" key={recipientType}>
                                                    <p>{label}</p>
                                                    {recipientType !== 'referringOrg' && (
                                                        <div className="flex gap-4">
                                                            <div className="flex-1">
                                                                <Input
                                                                    type="text"
                                                                    name={`careTeam.${index}.recipientName`}
                                                                    onChange={handleChange}
                                                                    value={values.careTeam[index].recipientName}
                                                                    label={'Name'}
                                                                    hideValidationIcon
                                                                ></Input>
                                                            </div>
                                                            <div className="flex-1">
                                                                <Input
                                                                    type="text"
                                                                    name={`careTeam.${index}.recipientEmail`}
                                                                    onChange={handleChange}
                                                                    value={values.careTeam[index].recipientEmail}
                                                                    label={'Email address'}
                                                                    hideValidationIcon
                                                                ></Input>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        }
                                    </FieldArray>
                                </div>
                            </CardBody>
                            <CardFooter>
                                <div className="flex justify-between items-center">
                                    <Button onClick={() => onBack(values)} variant="tertiary">
                                        <div className="flex items-center gap-2 text-green-500">
                                            <IconChevron />
                                            <span>Back</span>
                                        </div>
                                    </Button>
                                    <Button isProcessing={isSubmitting} type="submit" disabled={isSubmitting}>
                                        Submit
                                    </Button>
                                </div>
                            </CardFooter>
                        </Card>
                    </form>
                )}
            </Formik>
        </div>
    );
}
